import React, { PropsWithChildren } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { Insulin } from "@/models/diabetes/InsulinModel.ts";
import { InsulinBasalRate } from "@/models/InsulinBasalRate.ts";
import {
  ClosedLoopUserMode,
  PumpEvent,
  getPumpEventTranslation,
  getPumpModeTranslation,
  getUserModeTranslation,
} from "@/models/InsulinPumpEvents.ts";
import { SvgDataListTooltip } from "@/uiKit/atoms/svg/SvgDataListTooltip.tsx";
import {
  SvgDataTooltip,
  SvgDataTooltipFooter,
  SvgDataTooltipKeyValueContent,
  SvgDataTooltipTitle,
} from "@/uiKit/atoms/svg/SvgDataTooltip.tsx";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { formatNumber } from "@/utils/mathUtils.ts";

const PumpBolusContent: React.FC<{ insulin: Insulin }> = ({ insulin }) => {
  const { t } = useTranslation();
  let carbs = null;
  let correction = null;
  if (insulin.bolusSettings?.type === "carbs" || insulin.bolusSettings?.type === "carbs_and_correction") {
    carbs = (
      <>
        {insulin.bolusSettings.carbs ? (
          <SvgDataTooltipKeyValueContent
            label={t("insulin.carbs")}
            value={`${formatNumber(insulin.bolusSettings.carbs, 1)} g`}
            emphasis="none"
          />
        ) : null}
        {insulin.bolusSettings.carbsRatio ? (
          <SvgDataTooltipKeyValueContent
            label={t("insulin.carbsRatio")}
            value={`${formatNumber(insulin.bolusSettings.carbsRatio, 1)} g/U`}
            emphasis="none"
          />
        ) : null}
        <SvgDataTooltipKeyValueContent
          label={t("insulin.foodInsulin")}
          value={`${formatNumber(insulin.bolusSettings.foodInsulin, 1)} U`}
          emphasis="none"
        />
      </>
    );
  }
  if (insulin.bolusSettings?.type === "correction" || insulin.bolusSettings?.type === "carbs_and_correction") {
    correction = (
      <>
        {insulin.bolusSettings.glycemia ? (
          <SvgDataTooltipKeyValueContent
            label={t("insulin.glycemia")}
            value={`${formatNumber(insulin.bolusSettings.glycemia, 0)} mg/dL`}
            emphasis="none"
          />
        ) : null}
        {insulin.bolusSettings.insulinSensitivity ? (
          <SvgDataTooltipKeyValueContent
            label={t("insulin.insulinSensitivity")}
            value={`${formatNumber(insulin.bolusSettings.insulinSensitivity, 1)} mg/dL/U`}
            emphasis="none"
          />
        ) : null}
        {insulin.bolusSettings.targetGlycemia ? (
          <SvgDataTooltipKeyValueContent
            label={t("insulin.targetGlycemia")}
            value={`${formatNumber(insulin.bolusSettings.targetGlycemia, 0)} mg/dL`}
            emphasis="none"
          />
        ) : null}
        {insulin.bolusSettings?.iob ? (
          <SvgDataTooltipKeyValueContent
            label={t("insulin.iob")}
            value={`${formatNumber(insulin.bolusSettings.iob, 1)} U`}
            emphasis="none"
          />
        ) : null}
        <SvgDataTooltipKeyValueContent
          label={t("insulin.correction")}
          value={`${formatNumber(insulin.bolusSettings.correctionInsulin, 1)} U`}
          emphasis="none"
        />
      </>
    );
  }

  return (
    <>
      {correction}
      {carbs}
      <SvgDataTooltipKeyValueContent
        label={t("insulin.quantity")}
        value={`${formatNumber(insulin.quantity, 1)} U`}
        emphasis="all"
      />
    </>
  );
};

const InsulinTooltipContent: React.FC<{ item: Insulin }> = ({ item }) => {
  switch (true) {
    case item.reason === "basal":
      return (
        <SvgDataTooltip>
          <SvgDataTooltipTitle title={`${formatNumber(item.quantity, 1)} U`} />
          <SvgDataTooltipFooter date={item.date} inputMethod={item.deviceKind} />
        </SvgDataTooltip>
      );
    case item.reason === "bolus":
      return (
        <SvgDataTooltip>
          <PumpBolusContent insulin={item} />
          <SvgDataTooltipFooter date={item.date} inputMethod={item.deviceKind} />
        </SvgDataTooltip>
      );
    default:
      return null;
  }
};

export type BasalRateGraphTooltipProps = PropsWithChildren<{
  item: InsulinBasalRate;
  date?: DateTime;
}>;

export const BasalRateGraphTooltip: React.FC<BasalRateGraphTooltipProps> = ({ item, date, children }) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      popupId="basal-rate-graph-tooltip"
      svg
      placement="top"
      dismiss={false}
      autoOpen
      offset={0}
      content={
        <SvgDataTooltip>
          <SvgDataTooltipTitle title={`${formatNumber(item.basalRate, 2)} U/h`} />
          {item.tempRate && (
            <SvgDataTooltipKeyValueContent
              label={t("pumpEvents.tempRatePercent")}
              value={`${formatNumber(item.tempRatePercent * 100, 0)} %`}
              emphasis="none"
            />
          )}
          <SvgDataTooltipFooter date={date ?? item.date} inputMethod={"pump"} />
        </SvgDataTooltip>
      }
    >
      {children}
    </Tooltip>
  );
};

export type InsulinPointTooltipProps = PropsWithChildren<{
  insulins: Insulin[];
  x?: number;
  y?: number;
}>;

export const InsulinPointTooltip: React.FC<InsulinPointTooltipProps> = ({ insulins, x, y, children }) => (
  <Tooltip
    popupId="insulin-point-tooltip"
    svg
    placement="top"
    relativeClientPoint
    clientPoint={{
      enabled: true,
      x: x ?? 0,
      y: y ?? 0,
    }}
    content={
      <SvgDataListTooltip>
        {insulins.map(item => (
          <InsulinTooltipContent key={item.id} item={item} />
        ))}
      </SvgDataListTooltip>
    }
  >
    {children}
  </Tooltip>
);

export const DiscreteInsulinPointTooltip: React.FC<InsulinPointTooltipProps> = ({ insulins, x, y, children }) => (
  <Tooltip
    popupId="discrete-insulin-point-tooltip"
    svg
    placement="top"
    relativeClientPoint
    clientPoint={{
      enabled: true,
      x: x ?? 0,
      y: y ?? 0,
    }}
    content={
      <SvgDataListTooltip>
        {insulins.map(item => (
          <SvgDataTooltip key={item.id}>
            <SvgDataTooltipTitle title={`${formatNumber(item.quantity, 1)} U`} />
            <SvgDataTooltipFooter date={item.date} inputMethod={item.deviceKind} />
          </SvgDataTooltip>
        ))}
      </SvgDataListTooltip>
    }
  >
    {children}
  </Tooltip>
);

const PumpEventTooltipContent: React.FC<{ item: PumpEvent }> = ({ item }) => {
  const { t } = useTranslation();

  switch (item.category) {
    case "cartridge_filled":
      return item.insulinActual ? (
        <SvgDataTooltipKeyValueContent
          label={t("pumpEvents.insulin")}
          value={`${formatNumber(item.insulinActual, 1)} U`}
        />
      ) : null;

    case "cannula_filled":
    case "tubing_filled":
      return item.primeSize ? (
        <SvgDataTooltipKeyValueContent
          label={t("pumpEvents.primeSize")}
          value={`${formatNumber(item.primeSize, 1)} U`}
        />
      ) : null;
    default:
      return null;
  }
};

export type PumpEventTooltipProps = PropsWithChildren<{
  events: PumpEvent[];
  x?: number;
  y?: number;
}>;

export const PumpEventTooltip: React.FC<PumpEventTooltipProps> = ({ events, x, y, children }) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      popupId="pump-event-tooltip"
      svg
      placement="top"
      relativeClientPoint
      clientPoint={{
        enabled: true,
        x: x ?? 0,
        y: y ?? 0,
      }}
      content={
        <SvgDataListTooltip>
          {events.map(item => (
            <SvgDataTooltip key={item.id}>
              <SvgDataTooltipKeyValueContent
                label={t("pumpEvents.category")}
                value={t(getPumpEventTranslation(item.category))}
              />
              <PumpEventTooltipContent item={item} />
              <SvgDataTooltipFooter date={item.date} />
            </SvgDataTooltip>
          ))}
        </SvgDataListTooltip>
      }
    >
      {children}
    </Tooltip>
  );
};

export type ClosedLoopPumpModeTooltipProps = PropsWithChildren<{
  pumpMode: "manual" | "closed_loop";
  date: string;
  endDate: string;
  x?: number;
  y?: number;
}>;

export const ClosedLoopPumpModeTooltip: React.FC<ClosedLoopPumpModeTooltipProps> = ({
  pumpMode,
  date,
  endDate,
  children,
}) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      popupId="closed-loop-pump-mode-tooltip"
      svg
      placement="top"
      relativeClientPoint
      clientPoint={{
        enabled: true,
      }}
      content={
        <SvgDataTooltip>
          <SvgDataTooltipTitle title={t(getPumpModeTranslation(pumpMode))} />
          <SvgDataTooltipFooter date={date} endDate={endDate} />
        </SvgDataTooltip>
      }
    >
      {children}
    </Tooltip>
  );
};

export type ClosedLoopUserModeTooltipProps = PropsWithChildren<{
  mode: ClosedLoopUserMode;
  date: string;
  endDate: string;
  x?: number;
  y?: number;
}>;

export const ClosedLoopUserModeTooltip: React.FC<ClosedLoopUserModeTooltipProps> = ({
  mode,
  date,
  endDate,
  children,
}) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      popupId="closed-loop-user-mode-tooltip"
      svg
      placement="top"
      relativeClientPoint
      clientPoint={{
        enabled: true,
      }}
      content={
        <SvgDataTooltip>
          <SvgDataTooltipTitle title={t(getUserModeTranslation(mode))} />
          <SvgDataTooltipFooter date={date} endDate={endDate} />
        </SvgDataTooltip>
      }
    >
      {children}
    </Tooltip>
  );
};
